import { UseMutationOptions, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { minutes } from '@utils/milliseconds';

import { useFeature } from 'features/authentication/authentication-queries';
import { usePermissions } from 'features/permissions';

import {
  connect,
  ConnectType,
  getToken,
  ConnectError,
  disconnect,
  applicationJourneys,
  statistics,
  companyTravellerAttributes,
  users,
  UserType,
  list,
  AddToJourneyType,
  addToJourney,
  removeFromJourney,
} from './api';

export const tokenQueryKey = () => ['journeys-token'];

export const useInvalidateJourneys = () => {
  const queryClient = useQueryClient();
  const invalidateToken = () => queryClient.invalidateQueries(tokenQueryKey());
  const invalidateApplicant = (applicationId: number) =>
    queryClient.invalidateQueries(getApplicationJourneyQueryKey(applicationId));
  return { invalidateToken, invalidateApplicant };
};

// Every job route reads this through useJobTabs, and the request costs a
// round-trip to the Journeys API. Without a staleTime it refetches on each
// navigation between job tabs; connect/disconnect calls invalidateToken(),
// which overrides staleTime, so the connected badge still updates instantly.
export const TOKEN_STALE_TIME = minutes(5);

export const useToken = () => {
  return useQuery({
    queryKey: tokenQueryKey(),
    queryFn: getToken,
    staleTime: TOKEN_STALE_TIME,
  });
};

/**
 * `ok` and `valid` are separate flags — credentials that are present but no
 * longer authenticate come back as {ok: true, valid: false}. Both are required,
 * matching the send sidebar's own isConnected and use-stage-entry-journey's
 * gate. Gating on `ok` alone renders the whole automation surface as connected
 * and every action behind it fails.
 */
export const isJourneysTokenConnected = (token: { ok?: boolean; valid?: boolean } | undefined) =>
  Boolean(token?.ok && token.valid);

export const useIsJourneysEnabled = () => {
  const tokenQuery = useToken();
  return isJourneysTokenConnected(tokenQuery.data);
};

/**
 * Gate for all trigger-related UI: the Journeys integration feature flag, the
 * role permission to manage journey triggers, and a connected Journeys
 * integration. False while the connection check is still in flight.
 */
export const useHasJourneysAutomation = () => {
  const hasIntegrationFlag = useFeature('FEATURE_INTEGRATION_JOURNEYS');
  const { hasRolePermission } = usePermissions();
  const isJourneysEnabled = useIsJourneysEnabled();
  return hasIntegrationFlag && hasRolePermission('application:add-journeys') && isJourneysEnabled;
};

/** True for roles allowed to manage the company Apps section. */
export const useIsAppsAdmin = () => {
  const { hasRolePermission } = usePermissions();
  return hasRolePermission('company:apps');
};

export const useConnect = (options?: UseMutationOptions<unknown, unknown, ConnectType>) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateToken } = useInvalidateJourneys();

  return useMutation<unknown, ConnectError, ConnectType>({
    ...options,
    mutationFn: connect,
    onSuccess: (...data) => {
      showSuccess(t('journeysConnected'));
      invalidateToken();
      options?.onSuccess?.(...data);
    },
    onError: (data, ...rest) => {
      if (data.response?.data.error) {
        showError(data.response.data.error);
      } else {
        showError(t('journeysNotConnected'));
      }
      options?.onError?.(data, ...rest);
    },
  });
};

export const useDisconnect = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateToken } = useInvalidateJourneys();

  return useMutation({
    mutationFn: disconnect,
    onSuccess: (...data) => {
      showSuccess(t('journeysDisconnected'));
      invalidateToken();
    },
    onError: (data, ...rest) => {
      showError(t('journeysNotDisconnected'));
    },
  });
};

const getApplicationJourneyQueryKey = (applicationId: number) => [
  'journeys-application',
  applicationId,
];

export const useApplicationJourneys = (applicationId: number) => {
  return useQuery({
    queryKey: getApplicationJourneyQueryKey(applicationId),
    queryFn: () => applicationJourneys(applicationId),
  });
};

const journeysStatsQueryKey = ['journeys-stats'];
export const useJourneysStats = () => {
  return useQuery({
    queryKey: journeysStatsQueryKey,
    queryFn: () => statistics(),
  });
};

const companyTravellerAttributesQueryKey = ['company-traveller-attributes'];
export const useCompanyTravellerAttributes = () => {
  return useQuery({
    queryKey: companyTravellerAttributesQueryKey,
    queryFn: () => companyTravellerAttributes(),
  });
};

const usersQueryKey = ['journeys-users'];

/**
 * A user invited to Journeys who has never logged in comes back from the
 * Journeys `/v1/users/` endpoint with `name: null` — `CompanyUserSerializer
 * .to_representation` (api/journeys/api/v1/users/serializers.py) blanks the
 * name deliberately for anyone with `last_login is None`. Both the
 * value-input `user` attribute Select and the add-to-journey sidebar's user
 * list use `user.name` directly as the option label, so an unfiltered user
 * renders as a blank line. Filtering once here, rather than in each
 * consumer, is what keeps them from being able to regress independently.
 */
const hasDisplayName = (user: UserType) => Boolean(user.name && user.name.trim());

export const useUsers = () => {
  return useQuery({
    queryKey: usersQueryKey,
    queryFn: async () => (await users()).filter(hasDisplayName),
  });
};

const getListQueryKey = (listId: string) => ['journey-list', listId];

export const useList = (listId: string) => {
  return useQuery({
    queryKey: getListQueryKey(listId),
    queryFn: () => list(listId),
    enabled: Boolean(listId),
  });
};

export const useAddToJourney = (
  options?: UseMutationOptions<unknown, unknown, AddToJourneyType>
) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const { invalidateApplicant } = useInvalidateJourneys();
  return useMutation({
    ...options,
    mutationFn: (data) => addToJourney(data),
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      if (variables.applications.length === 1) {
        invalidateApplicant(variables.applications[0]);
      }
    },
    onError: () => {
      showError(t('travellersNotAdded'));
    },
  });
};

export const useRemoveFromJourneys = (applicationId: number, travellerJourneyId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateApplicant } = useInvalidateJourneys();
  return useMutation({
    mutationFn: () => removeFromJourney(applicationId, travellerJourneyId),
    onSuccess: () => {
      invalidateApplicant(applicationId);
      showSuccess(t('journeyDeleted'));
    },
    onError: () => {
      showError(t('journeyNotDeleted'));
    },
  });
};

const locale = {
  en: {
    journeysConnected: 'Journeys connected',
    journeysNotConnected: 'Journeys not connected',
    journeysDisconnected: 'Journeys has been removed',
    journeysNotDisconnected: 'Journeys could not been removed',
    travellersNotAdded: 'Travellers could not be added',
    journeyDeleted: 'Journey has been deleted',
    journeyNotDeleted: 'Journey could not be deleted',
  },
  is: {
    journeysConnected: 'Journeys tengt',
    journeysNotConnected: 'Journeys ekki tengt',
    journeysDisconnected: 'Journeys hefur verið aftengt',
    journeysNotDisconnected: 'Journeys var ekki hægt að aftengja',
    travellersNotAdded: 'Ekki var hægt að bæta við Travellers',
    journeyDeleted: 'Journey hefur verið eytt',
    journeyNotDeleted: 'Journey var ekki hægt að eyða',
  },
};
